// /mnt/mantenimientos/frontend/src/modules/MantenimientosModule.jsx
import React, { useEffect, useMemo, useState } from "react";
import { isOperationalEquipment } from "../utils/equipmentLifecycle";
import { useCliente } from "../context/ClienteContext";
import { actualizarInfoOperativaOrdenador, getMiTrabajoMantenimiento } from "../api";

import PcCard from "../componentsTecnico/PcCard";
import ModuleHeader from "../components/ui/ModuleHeader";
import EntityIndexItem from "../components/ui/EntityIndexItem";
import "../styles/modules/mantenimientos.css";
import { MODULE_IDS } from "./moduleIds";

function getProblemsCount(z) {
  if (!z) return 0;
  const metricas = z?.metricas && typeof z.metricas === "object" ? z.metricas : z;

  const probs = Array.isArray(metricas?.problems)
    ? metricas.problems
    : Array.isArray(z?.top_problems)
    ? z.top_problems
    : [];

  return probs?.length || 0;
}


function GlobalMaintenanceQueue({ onOpen }) {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [error, setError] = useState("");
  const load = async () => { setLoading(true); setError(""); try { const response = await getMiTrabajoMantenimiento(); setRows(Array.isArray(response.data) ? response.data : []); } catch (e) { setRows([]); setError(e?.response?.data?.error || e.message || "No se pudo cargar la cola operativa."); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const totalPending = rows.reduce((sum, row) => sum + Math.max(0, Number(row.total_equipment || 0) - Number(row.completed_count || 0)), 0);
  return <div className="maint-global"><ModuleHeader eyebrow="Operación" title="Mantenimientos" description="Cola operativa de todos los clientes autorizados." actions={<button className="button secondary" onClick={load} disabled={loading} type="button">{loading ? "Actualizando…" : "Actualizar"}</button>}><span className="equiposScope">Todos los clientes</span></ModuleHeader><section className="card maint-global-card"><header className="maint-global-head"><div><h2>Trabajo pendiente</h2><p>{loading ? "Cargando cola…" : String(totalPending) + " equipos pendientes en " + String(rows.length) + " clientes"}</p></div></header>{error ? <div className="equiposFormError">{error}</div> : null}{!loading && !error && !rows.length ? <div className="mod-mant-empty">No hay mantenimientos pendientes en el ámbito actual.</div> : null}<div className="maint-global-list">{rows.map((row) => { const pending=Math.max(0,Number(row.total_equipment||0)-Number(row.completed_count||0)); return <button className="maint-global-row" key={row.cliente_id} onClick={() => onOpen(row)} type="button"><div><strong>{row.cliente}</strong><span>{row.next_equipment ? "Siguiente: " + row.next_equipment : "Abrir mantenimiento"}</span></div><div className="maint-global-row__meta"><span>{String(pending) + " pendiente" + (pending===1 ? "" : "s")}</span><span className="maint-global-row__action">Abrir →</span></div></button>; })}</div></section></div>;
}

export default function MantenimientosModule() {
  const {
    ordenadores,
    clienteSeleccionadoId,
    clienteInfo,
    seleccionarCliente,
    setActiveModule,
    pcActivo,
    setPcActivo,
    maintenanceFocus,
    estado,
    setEstado,
    tieneMantenimientoMesActual,

    guardarMantenimiento,

    finalizarCliente,

    zabbixByPc,
    loadingZabbix,
    refreshZabbixCliente,
    refreshOrdenadores,
  } = useCliente();
  const equiposOperativos = useMemo(
    () => (ordenadores || []).filter(isOperationalEquipment),
    [ordenadores]
  );

  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("prioridad"); // prioridad | pendientes | incidencias | todos
  const [pendingTarget, setPendingTarget] = useState(null);

  const pcActual = useMemo(() => {
    return equiposOperativos.find((p) => p.id === pcActivo) || null;
  }, [equiposOperativos, pcActivo]);

  useEffect(() => {
    if (!maintenanceFocus?.pcId) return;
    const target = equiposOperativos.find(
      (pc) => String(pc.id) === String(maintenanceFocus.pcId)
    );
    if (target && String(pcActivo) !== String(target.id)) setPcActivo(target.id);
  }, [maintenanceFocus, equiposOperativos, pcActivo, setPcActivo]);

  const kpis = useMemo(() => {
    const pcs = equiposOperativos;
    let pendientes = 0;

    for (const pc of pcs) {
      const done = tieneMantenimientoMesActual(pc.id);
      if (!done) pendientes++;
    }

    return {
      total: pcs.length,
      pendientes,
    };
  }, [equiposOperativos, tieneMantenimientoMesActual]);

  const ordenadoresFiltrados = useMemo(() => {
    const pcs = [...equiposOperativos];
    const query = String(q || "").trim().toLowerCase();

    function pcMeta(pc) {
      const done = tieneMantenimientoMesActual(pc.id);
      const z = zabbixByPc?.[String(pc.id)];
      const probCount = getProblemsCount(z);

      // score: incidencias > pendientes
      const score = (probCount > 0 ? 1000 + probCount : 0) + (!done ? 200 : 0);

      return { done, probCount, score };
    }

    const filtered = pcs.filter((pc) => {
      const label = `${pc.descripcion || ""} ${pc.hostname || ""} ${pc.ip || ""} ${pc.serial || ""}`.toLowerCase();
      if (query && !label.includes(query)) return false;

      const meta = pcMeta(pc);

      if (filtro === "pendientes") return !meta.done;
      if (filtro === "incidencias") return meta.probCount > 0;
      if (filtro === "todos") return true;

      return true; // prioridad
    });

    filtered.sort((a, b) => {
      const ma = pcMeta(a);
      const mb = pcMeta(b);

      if (filtro === "prioridad") {
        if (mb.score !== ma.score) return mb.score - ma.score;
      }

      return String(a.descripcion || a.hostname || "").localeCompare(
        String(b.descripcion || b.hostname || "")
      );
    });

    return filtered;
  }, [equiposOperativos, q, filtro, tieneMantenimientoMesActual, zabbixByPc]);

  const hasDraft = (pcId) => {
    const form = estado?.formularios?.[pcId] || {};
    const today = new Date().toISOString().slice(0, 10);
    const defaultTechnician = localStorage.getItem("usuario") || "";
    return Boolean(
      String(form.observaciones || "").trim() ||
      String(form.incidencias || "").trim() ||
      (form.fecha && form.fecha !== today) ||
      String(form.status || "completed") !== "completed" ||
      String(form.follow_up_date || "").trim() ||
      String(form.tecnico || "") !== String(defaultTechnician)
    );
  };

  const selectPc = (id, skipDraftCheck = false) => {
    if (String(id) === String(pcActivo)) return;
    if (!skipDraftCheck && hasDraft(pcActivo)) {
      setPendingTarget(id);
      return;
    }
    setPcActivo(id);
  };

  const irAlSiguientePendiente = (skipDraftCheck = false) => {
    if (!equiposOperativos.length) return;

    const currentIndex = equiposOperativos.findIndex(
      (pc) => String(pc.id) === String(pcActivo)
    );
    const ordered = currentIndex >= 0
      ? [...equiposOperativos.slice(currentIndex + 1), ...equiposOperativos.slice(0, currentIndex + 1)]
      : equiposOperativos;
    const next = ordered.find(
      (pc) => String(pc.id) !== String(pcActivo) && !tieneMantenimientoMesActual(pc.id)
    );

    if (next) selectPc(next.id, skipDraftCheck);
  };

  const ultimoPc = (pcId) => (estado?.mantenimientosPorPc?.[pcId] || [])[0] || null;
  const focusedMaintenance = (pcId) => {
    if (!maintenanceFocus?.maintenanceId || String(maintenanceFocus.pcId) !== String(pcId)) return null;
    return (estado?.mantenimientosPorPc?.[pcId] || []).find(
      (item) => String(item.id) === String(maintenanceFocus.maintenanceId)
    ) || null;
  };

  const setFormForPc = (pcId, campo, valor) => {
    setEstado((prev) => ({
      ...prev,
      formularios: {
        ...prev.formularios,
        [pcId]: {
          ...(prev.formularios?.[pcId] || {}),
          [campo]: valor,
        },
      },
    }));
  };

  if (!clienteSeleccionadoId) return <GlobalMaintenanceQueue onOpen={async (row) => { await seleccionarCliente(row.cliente_id); if (row.next_equipment_id) setPcActivo(row.next_equipment_id); setActiveModule(MODULE_IDS.MANTENIMIENTOS); }} />;

  return (
    <div className="mod-mant-page">
      <ModuleHeader
        eyebrow="Operación"
        title="Mantenimientos"
        description="Revisa el parte del equipo, registra la intervención y continúa con el siguiente pendiente."
        clientDetails
      >
        <span className="mp-context-pill">{clienteInfo?.nombre || "Cliente activo"}</span>
      </ModuleHeader>
      <div className="mod-mant">
      {/* ===== IZQUIERDA ===== */}
      <aside className="mod-mant-left card">
        <div className="mod-mant-left-head mp-panel-heading">
          <div>
            <div className="mod-mant-title">Mantenimientos</div>
            <div className="mod-mant-sub">
              {kpis.pendientes} pendientes de {kpis.total}
            </div>
          </div>
          <button
            className="button primary small"
            onClick={() => irAlSiguientePendiente(false)}
            disabled={kpis.pendientes < 1 || (kpis.pendientes === 1 && !tieneMantenimientoMesActual(pcActivo))}
            title="Ir al siguiente equipo pendiente del mes"
          >
            Siguiente
          </button>
        </div>

        <div className="mod-mant-filters">
          <input
            className="input mod-mant-search"
            type="text"
            placeholder="Buscar (desc/hostname/ip/serial)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select
            className="input mod-mant-select"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          >
            <option value="prioridad">Prioridad</option>
            <option value="pendientes">Pendientes</option>
            <option value="incidencias">Con incidencias</option>
            <option value="todos">Todos</option>
          </select>
        </div>

        <div className="mod-mant-list">
          {ordenadoresFiltrados.length === 0 ? (
            <div className="mod-mant-empty">No hay equipos con ese filtro/búsqueda.</div>
          ) : (
            ordenadoresFiltrados.map((pc) => {
              const active = pc.id === pcActivo;
              const done = tieneMantenimientoMesActual(pc.id);

              const z = zabbixByPc?.[String(pc.id)];
              const probCount = getProblemsCount(z);

              return <EntityIndexItem key={pc.id} selected={active} onClick={() => selectPc(pc.id)} title={pc.descripcion || pc.hostname || "Equipo " + pc.id} subtitle={[pc.hostname, pc.ip].filter(Boolean).join(" · ") || pc.usuario || "Sin hostname ni IP registrados"} status={{ label: done ? "Al día" : "Pendiente", tone: done ? "ok" : "pending" }} tags={[pc.so || "Sistema sin informar", pc.teamviewer_id ? "Acceso remoto" : null, probCount > 0 ? { label: "Incidencias " + probCount, tone: "warning" } : null]} />;
            })
          )}
        </div>
      </aside>

      {/* ===== DERECHA ===== */}
      <section className="mod-mant-right">
        {pcActual ? (
          <PcCard
            key={pcActual.id}
            pc={pcActual}
            ultimo={focusedMaintenance(pcActual.id) || ultimoPc(pcActual.id)}
            historial={estado?.mantenimientosPorPc?.[pcActual.id] || []}
            form={estado?.formularios?.[pcActual.id]}
            focusMaintenanceId={maintenanceFocus?.pcId && String(maintenanceFocus.pcId) === String(pcActual.id) ? maintenanceFocus.maintenanceId : null}
            onFormChange={(campo, valor) => setFormForPc(pcActual.id, campo, valor)}
            onGuardar={() => guardarMantenimiento(pcActual.id)}
            onGuardarYSiguiente={async () => {
              await guardarMantenimiento(pcActual.id);
              irAlSiguientePendiente(true);
            }}
            zabbix={zabbixByPc?.[String(pcActual.id)]}
            zabbixLoading={loadingZabbix}
            onRefreshZabbix={() => refreshZabbixCliente()}
            onUpdateInfo={async (value) => {
              await actualizarInfoOperativaOrdenador(pcActual.id, value);
              await refreshOrdenadores();
            }}
          />
        ) : (
          <div className="card" style={{ padding: 16 }}>
            Selecciona un equipo para comenzar.
          </div>
        )}
        {finalizarCliente && kpis.total > 0 && kpis.pendientes === 0 ? (
          <div className="mod-mant-complete">
            <span>Todos los equipos operativos están completados.</span>
            <button className="button primary" onClick={() => finalizarCliente()}>
              Finalizar cliente
            </button>
          </div>
        ) : null}
      </section>
      {pendingTarget ? (
        <div className="modal-overlay mod-mant-draft-overlay" onMouseDown={(event) => event.target === event.currentTarget && setPendingTarget(null)}>
          <div className="modal mod-mant-draft-modal" role="dialog" aria-modal="true" aria-labelledby="draft-title">
            <div className="modal-header"><h3 id="draft-title">Hay cambios sin guardar</h3></div>
            <div className="modal-body"><p>El parte actual contiene información que todavía no se ha guardado. Si continúas, el borrador permanecerá en memoria durante esta sesión.</p></div>
            <div className="modal-footer">
              <button className="button secondary" onClick={() => setPendingTarget(null)}>Seguir editando</button>
              <button className="button primary" onClick={() => { const target = pendingTarget; setPendingTarget(null); selectPc(target, true); }}>Cambiar de equipo</button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}
